import { useDeviceType } from "#src/hooks/use-device-type";
import { useLayoutFooterStyle, useLayoutHeaderStyle } from "#src/hooks/use-layout-style";
import { CustomSidebar } from "#src/layout/Sidebar/CustomSidebar";
import { usePreferencesStore } from "#src/store/preferences";
import { useTabsStore } from "#src/store/tabs";
import { cn } from "#src/utils/cn";
import { Grid } from "antd";
import { useEffect, useMemo } from "react";

import { footerHeight, headerHeight, tabbarHeight } from "../constants";
import { useLayout } from "../hooks";
import LayoutContent from "../layout-content";
import LayoutHeader from "../layout-header";
import LayoutMenu from "../layout-menu";
import { useMenu } from "../layout-menu/use-menu";
import LayoutMixedSidebar from "../layout-mixed-sidebar";
import LayoutMobileMenu from "../layout-mobile-menu";
import LayoutTabbar from "../layout-tabbar";
import { Logo } from "../widgets/logo";

const { useBreakpoint } = Grid;

/**
 * Please do not use this component through lazy, otherwise the switching routing page will flash.
 * NO:
 * const ContainerLayout = lazy(() => import("#src/layout/container-layout"));
 * YES:
 * import ContainerLayout from "#src/layout/container-layout";
 */
export default function ContainerLayout() {
	const screens = useBreakpoint();
	const { isTopNav, isTwoColumnNav, isMixedNav, sidebarWidth, sideCollapsedWidth, firstColumnWidthInTwoColumnNavigation } = useLayout();
	const isMaximize = useTabsStore(state => state.isMaximize);
	const { /* watermark, watermarkContent, enableFooter, fixedFooter, enableBackTopButton, */tabbarEnable, sidebarEnable, sidebarCollapsed, setPreferences } = usePreferencesStore();
	const { isMobile } = useDeviceType();
	const { sideNavItems, topNavItems, handleMenuSelect, sideNavMenuKeyInSplitMode } = useMenu();

	const { setLayoutHeaderHeight } = useLayoutHeaderStyle();
	const { setLayoutFooterHeight } = useLayoutFooterStyle();

	useEffect(() => {
		/* iPad */
		if (screens.lg && !screens.xl) {
			setPreferences("sidebarCollapsed", true);
		}
		/* PC */
		else if (screens.xl) {
			setPreferences("sidebarCollapsed", false);
		}
		/* Mobile */
		else if (screens.xs || (screens.sm && !screens.md)) {
			setPreferences("sidebarCollapsed", false);
		}
	}, [screens]);

	const sidebarEnableState = useMemo(() => !isTopNav && sidebarEnable, [isTopNav, sidebarEnable]);
	const computedSidebarWidth = useMemo(() => {
		if (isMaximize || isMobile) {
			return 0;
		}
		const currentSidebarWidth = sidebarCollapsed ? sideCollapsedWidth : sideCollapsedWidth;
		if (isTwoColumnNav) {
			/* Two-column navigation, default width for the first column */
			return currentSidebarWidth + (firstColumnWidthInTwoColumnNavigation ?? 0);
		}
		if (sidebarEnableState) {
			return currentSidebarWidth;
		}
		return 0;
	}, [
		// Mobile
		isMobile,
		isMaximize,
		isTwoColumnNav,
		sidebarEnableState,
		sidebarWidth,
		sidebarCollapsed,
		sideCollapsedWidth,
		firstColumnWidthInTwoColumnNavigation,
	]);

	/**
	 * Calculate the height of header and tabbar
	 */
	const headerWrapperHeight = useMemo(() => {
		let height = headerHeight;
		if (tabbarEnable) {
			height += tabbarHeight;
		}
		return height;
	}, [tabbarEnable, tabbarHeight]);

	useEffect(() => {
		setLayoutHeaderHeight(isMaximize ? tabbarHeight : headerWrapperHeight);
	}, [headerWrapperHeight, isMaximize]);

	useEffect(() => {
		setLayoutFooterHeight(footerHeight);
	}, []);

	return (
		<>
			<section
				// style={{
				// 	paddingLeft: computedSidebarWidth,
				// }}
				className={cn(
					"transition-all flex flex-col h-screen",
				)}
			>
				<LayoutHeader>
					{isTopNav || isMixedNav
						? (
							<>
								{isTopNav ? <Logo sidebarCollapsed={false} className="mr-8" /> : null}
								<LayoutMenu mode="horizontal" menus={topNavItems} handleMenuSelect={handleMenuSelect} />
							</>
						)
						: null}
				</LayoutHeader>
				{tabbarEnable ? <LayoutTabbar computedSidebarWidth={computedSidebarWidth} /> : null}

				{/* Mobile Menu */}
				<LayoutMobileMenu />

				{/* PC */}
				{sidebarEnableState && !isTwoColumnNav && !isMobile && <CustomSidebar items={sideNavItems} handleMenuSelect={handleMenuSelect} />}

				{
					isTwoColumnNav
						? (
							<LayoutMixedSidebar
								sideNavMenuKeyInSplitMode={sideNavMenuKeyInSplitMode}
								computedSidebarWidth={computedSidebarWidth}
								sideNavItems={sideNavItems}
								topNavItems={topNavItems}
								handleMenuSelect={handleMenuSelect}
							/>
						)
						: null
				}

				<LayoutContent computedSidebarWidth={computedSidebarWidth} />
			</section>

		</>
	);
}
